// ─── A4 Pagination Engine ──────────────────────────────────────────────────────
// Produces print-safe A4 page layouts for the thesis export preview.
//
// A4 dimensions at 96 dpi:
//   210mm = 793.7px  ≈  794px  wide
//   297mm = 1122.5px ≈ 1123px  tall
//
// Printable area (with standard thesis margins):
//   Left:   1.5in = 144px
//   Right:  1.0in =  96px
//   Top:    1.0in =  96px  (+ 0.4in header clearance → 134px effective)
//   Bottom: 1.0in =  96px  (+ 0.4in footer clearance → 134px effective)
//
//   Usable width:  794 - 144 - 96  = 554px
//   Usable height: 1123 - 134 - 134 = 855px

// ── Physical constants ────────────────────────────────────────────────────────

export const A4 = {
  width:         794,   // px at 96dpi
  height:        1123,  // px at 96dpi
  marginLeft:    144,   // 1.5in
  marginRight:   96,    // 1.0in
  marginTop:     96,    // 1.0in
  marginBottom:  96,    // 1.0in
  headerClear:   38,    // space below header text to content
  footerClear:   38,    // space above footer text from content
  get usableWidth()  { return this.width  - this.marginLeft - this.marginRight },
  get usableHeight() { return this.height - this.marginTop  - this.marginBottom - this.headerClear - this.footerClear },
}

// ── CSS for A4 page containers ────────────────────────────────────────────────

export const A4_PAGE_CSS = `
.a4-page-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #e8e8e8;
  padding: 2rem 1rem;
  min-height: 100vh;
}

.a4-page {
  width: ${A4.width}px;
  height: ${A4.height}px;
  background: white;
  box-shadow: 0 4px 24px rgba(0,0,0,0.2);
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid #d1d5db;
  font-family: "Times New Roman", Georgia, serif;
  font-size: 12pt;
  line-height: 2;
  color: #000;
  box-sizing: border-box;
}

.a4-page.a4-page--overflow {
  border: 2px solid #ef4444;
}

.a4-content-area {
  position: absolute;
  top: ${A4.marginTop + A4.headerClear}px;
  left: ${A4.marginLeft}px;
  right: ${A4.marginRight}px;
  bottom: ${A4.marginBottom + A4.footerClear}px;
  overflow: hidden;
}

.a4-header {
  position: absolute;
  top: ${Math.round(A4.marginTop * 0.5)}px;
  left: ${A4.marginLeft}px;
  right: ${A4.marginRight}px;
  font-size: 9pt;
  color: #888;
  text-align: right;
  font-family: "Times New Roman", serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.a4-footer {
  position: absolute;
  bottom: ${Math.round(A4.marginBottom * 0.5)}px;
  left: ${A4.marginLeft}px;
  right: ${A4.marginRight}px;
  font-size: 9pt;
  color: #666;
  text-align: center;
  font-family: "Times New Roman", serif;
}

.a4-overflow-flag {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-family: sans-serif;
  padding: 2px 8px;
  font-weight: 700;
  z-index: 10;
}

/* Content styles within A4 pages */
.a4-content-area h1.chapter-heading {
  font-size: 14pt;
  font-weight: bold;
  text-align: center;
  text-transform: uppercase;
  margin: 0 0 1.5rem;
}
.a4-content-area h2 { font-size: 12pt; font-weight: bold; margin: 1.25rem 0 0.5rem; }
.a4-content-area h3 { font-size: 12pt; font-weight: bold; font-style: italic; margin: 1rem 0 0.4rem; }
.a4-content-area h4 { font-size: 12pt; font-weight: bold; margin: 0.75rem 0 0.3rem; }
.a4-content-area p  { margin: 0 0 0.25rem; text-align: justify; text-indent: 0.5in; font-size: 12pt; }
.a4-content-area table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 10pt; }
.a4-content-area th { background: #f0ece4; font-weight: bold; border: 1px solid #999; padding: 4px 8px; }
.a4-content-area td { border: 1px solid #999; padding: 4px 8px; }
.a4-content-area blockquote { border-left: 2px solid #666; padding-left: 0.75rem; margin: 0.75rem 1rem; font-style: italic; }
.a4-content-area ul, .a4-content-area ol { padding-left: 1.5rem; margin: 0 0 0.5rem; }
.a4-content-area li { margin-bottom: 0.1rem; }
.a4-content-area .thesis-fig {
  margin: 1rem 0;
  text-align: center;
  border: 1px solid #d0cdc7;
  border-radius: 4px;
  padding: 0.5rem;
  background: #f8f6f2;
}
.a4-content-area .thesis-fig .fig-label { font-weight: bold; font-size: 10pt; color: #1e3a5f; }
.a4-content-area .thesis-fig .fig-cap   { font-style: italic; font-size: 9pt; color: #555; margin-top: 3px; }
.a4-content-area .lof-entry {
  display: flex; align-items: baseline; margin-bottom: 0.35rem; gap: 0.25rem;
}
.a4-content-area .lof-label { white-space: nowrap; min-width: 90px; font-style: italic; color: #333; }
.a4-content-area .lof-title { flex: 0 1 auto; }
.a4-content-area .lof-dots  { flex: 1; border-bottom: 1px dotted #666; margin: 0 6px; min-width: 20px; }
.a4-content-area .toc-entry { margin-bottom: 0.35rem; }
.a4-content-area .toc-chapter { font-weight: bold; }
.a4-content-area .toc-section { margin-left: 1.5rem; }

@media print {
  .a4-page-wrapper { background: white; padding: 0; }
  .a4-page {
    width: 210mm; height: 297mm;
    margin: 0; box-shadow: none; border: none;
    page-break-after: always;
  }
  .a4-overflow-flag { display: none; }
}
`

// ── DOM-based height measurer ─────────────────────────────────────────────────

const MEASUREMENT_STYLE = `
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  width: ${A4.usableWidth}px;
  font-family: "Times New Roman", Georgia, serif;
  font-size: 12pt;
  line-height: 2;
  overflow: hidden;
`

/**
 * Measure the rendered height of an HTML string in a hidden div.
 * Browser-only. Returns the height in pixels.
 */
function measureHeight(html) {
  if (typeof document === 'undefined') return 200
  const div = document.createElement('div')
  div.style.cssText = MEASUREMENT_STYLE
  div.innerHTML = html
  document.body.appendChild(div)
  const h = div.scrollHeight
  document.body.removeChild(div)
  return h
}

// ── Page break detection ──────────────────────────────────────────────────────

const HARD_BREAK_RE = /(?:page-break-before:\s*always|break-before:\s*page)/i

function isChapterStart(html) {
  return /<h1\s[^>]*class="[^"]*chapter-heading/i.test(html) ||
         /<h1\s[^>]*>CHAPTER/i.test(html)
}

// ── DOM-based paginator ───────────────────────────────────────────────────────

/**
 * Split an array of HTML block strings into page buckets.
 * Each bucket contains HTML strings that fit within one A4 page's usable height.
 *
 * @param {string[]} blocks  — individual HTML block strings
 * @param {number}   pageH   — usable page height in px (defaults to A4.usableHeight)
 * @returns {string[][]}     — array of pages, each page is an array of block strings
 */
export function paginateBlocks(blocks, pageH = A4.usableHeight) {
  const pages = [[]]
  let currentHeight = 0

  for (const block of blocks) {
    const isBreakBefore = HARD_BREAK_RE.test(block) || isChapterStart(block)

    if (isBreakBefore && pages[pages.length - 1].length > 0) {
      pages.push([])
      currentHeight = 0
    }

    const blockH = measureHeight(block)

    // If block is taller than a full page, give it its own page
    if (blockH > pageH) {
      if (pages[pages.length - 1].length > 0) {
        pages.push([])
        currentHeight = 0
      }
      pages[pages.length - 1].push(block)
      pages.push([])
      currentHeight = 0
      continue
    }

    // Widow/orphan: if less than 120px remaining on page, break to next page
    // to avoid stranded single lines
    if (currentHeight + blockH > pageH - 60 && pages[pages.length - 1].length > 0) {
      pages.push([])
      currentHeight = 0
    }

    pages[pages.length - 1].push(block)
    currentHeight += blockH
  }

  // Remove trailing empty pages
  while (pages.length > 1 && pages[pages.length - 1].length === 0) {
    pages.pop()
  }

  return pages
}

/**
 * Split a single HTML string into block-level elements suitable for pagination.
 * Returns an array of HTML strings, one per block element.
 */
export function splitHtmlIntoBlocks(html) {
  if (typeof document === 'undefined') return [html]

  const parser = new DOMParser()
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html')
  const root = doc.querySelector('div')
  const blocks = []

  for (const child of root.children) {
    blocks.push(child.outerHTML)
  }

  return blocks.length > 0 ? blocks : [html]
}

/**
 * Compose a full thesis into A4 pages suitable for the preview renderer.
 * Returns array of { pageNumber, html, isOverflowing } objects.
 *
 * @param {{
 *   composedDoc:  ComposedDocument,
 *   frontMatterHtml: string,   — pre-rendered front matter HTML (from profile engine)
 *   figureRegistry: Map,
 *   tableRegistry:  Map,
 *   renderChapterHtml: function, — takes (sanitizedHtml, figureRegistry, tableRegistry) → string
 * }} params
 * @returns {Array<{pageNumber, html, isOverflowing}>}
 */
export function composeA4Pages({
  composedDoc,
  frontMatterHtml = '',
  renderChapterHtml,
}) {
  const { thesis, chapters, figureRegistry, tableRegistry, toc, lof, lot } = composedDoc

  const allBlocks = []

  // ── Front matter blocks ────────────────────────────────────────────────────
  if (frontMatterHtml) {
    // Front matter comes pre-split into fm-page divs — treat each as one page
    const fmPages = frontMatterHtml.split(/(?=<div\s[^>]*class="[^"]*fm-page)/i)
    fmPages.forEach(p => { if (p.trim()) allBlocks.push(p.trim()) })
  }

  // ── TOC ────────────────────────────────────────────────────────────────────
  allBlocks.push(buildInlineToc(toc, lof.length > 0, lot.length > 0))

  // ── LOF ────────────────────────────────────────────────────────────────────
  if (lof.length > 0) allBlocks.push(buildInlineLof(lof))

  // ── LOT ────────────────────────────────────────────────────────────────────
  if (lot.length > 0) allBlocks.push(buildInlineLot(lot))

  // ── Chapters ───────────────────────────────────────────────────────────────
  for (const ch of chapters) {
    const rendered = renderChapterHtml
      ? renderChapterHtml(ch.sanitizedHtml, figureRegistry, tableRegistry)
      : ch.sanitizedHtml

    // Chapter heading is always a hard page break
    const headingHtml = `<h1 class="chapter-heading" style="page-break-before:always">CHAPTER ${ch.chapterNumber}: ${ch.title}</h1>`
    allBlocks.push(headingHtml)

    const chapterBlocks = splitHtmlIntoBlocks(rendered)
    allBlocks.push(...chapterBlocks)
  }

  // ── Paginate ───────────────────────────────────────────────────────────────
  const pageBuckets = paginateBlocks(allBlocks)
  const thesisTitle = thesis?.title || 'PhD Thesis'

  return pageBuckets.map((blocks, i) => {
    const pageHtml = blocks.join('\n')
    const measuredH = measureHeight(pageHtml)
    return {
      pageNumber:   i + 1,
      html:         pageHtml,
      isOverflowing: measuredH > A4.usableHeight + 40,
      headerText:   thesisTitle,
    }
  })
}

// ── Inline list builders (used by composeA4Pages) ─────────────────────────────

function escapeHtml(s) {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function buildInlineToc(toc, hasFigs, hasTables) {
  const chEntries = toc.filter(e => e.level === 1)
  const entries = chEntries.map(e => {
    const subs = toc.filter(s => s.level === 2 && s.chapterNumber === e.chapterNumber)
    return `<div class="toc-entry toc-chapter">Chapter ${e.chapterNumber}: ${escapeHtml(e.title)}</div>
${subs.map(s => `<div class="toc-entry toc-section">${escapeHtml(s.title)}</div>`).join('\n')}`
  }).join('\n')

  return `<div style="page-break-before:always;padding:0.25rem 0">
<h2 style="text-align:center;font-size:14pt;font-weight:bold;text-transform:uppercase;margin-bottom:1.5rem">Table of Contents</h2>
<div class="toc-entry toc-chapter">Certificate</div>
<div class="toc-entry toc-chapter">Declaration</div>
<div class="toc-entry toc-chapter">Dedication</div>
<div class="toc-entry toc-chapter">Acknowledgements</div>
<div class="toc-entry toc-chapter">Abstract</div>
${hasFigs ? '<div class="toc-entry toc-chapter">List of Figures</div>' : ''}
${hasTables ? '<div class="toc-entry toc-chapter">List of Tables</div>' : ''}
${entries}
<div class="toc-entry toc-chapter" style="margin-top:0.5rem">References / Bibliography</div>
</div>`
}

function buildInlineLof(lof) {
  return `<div style="page-break-before:always;padding:0.25rem 0">
<h2 style="text-align:center;font-size:14pt;font-weight:bold;text-transform:uppercase;margin-bottom:1.5rem">List of Figures</h2>
${lof.map(f => `<div class="lof-entry">
  <span class="lof-label">${escapeHtml(f.displayLabel)}</span>
  <span class="lof-title">${escapeHtml(f.title || '')}</span>
  <span class="lof-dots"></span>
</div>`).join('\n')}
</div>`
}

function buildInlineLot(lot) {
  return `<div style="page-break-before:always;padding:0.25rem 0">
<h2 style="text-align:center;font-size:14pt;font-weight:bold;text-transform:uppercase;margin-bottom:1.5rem">List of Tables</h2>
${lot.map(t => `<div class="lof-entry">
  <span class="lof-label">${escapeHtml(t.displayLabel)}</span>
  <span class="lof-title">${escapeHtml(t.title || '')}</span>
  <span class="lof-dots"></span>
</div>`).join('\n')}
</div>`
}
